import React from 'react';
import { Box, Button, Typography, Container, CircularProgress, useTheme, alpha } from '@mui/material';
import { WifiOff, Refresh, CloudOff, SignalWifiOff } from '@mui/icons-material';
import { ConnectionErrorType } from '../../services/connectionService';

/**
 * Connection Error Screen Component
 * Full-screen component shown when critical connection errors prevent app usage
 * Used primarily on Login/Register pages where authentication cannot proceed
 */
const ConnectionErrorScreen = ({
  errorType = ConnectionErrorType.BACKEND_UNAVAILABLE,
  errorMessage = 'No se puede conectar con el servidor',
  onRetry,
  isRetrying = false,
  retryCount = 0
}) => {
  const theme = useTheme();
  const brandColor = theme.palette.brand?.main || '#1CB5D4';
  const brandDark = theme.palette.brand?.dark || '#18A3C0';

  // Get appropriate icon based on error type
  const getIcon = () => {
    switch (errorType) {
      case ConnectionErrorType.NETWORK_ERROR:
        return <SignalWifiOff sx={{ fontSize: 80, color: theme.palette.error.main }} />;
      case ConnectionErrorType.BACKEND_UNAVAILABLE:
        return <CloudOff sx={{ fontSize: 80, color: theme.palette.error.main }} />;
      case ConnectionErrorType.BACKEND_DEGRADED:
        return <WifiOff sx={{ fontSize: 80, color: theme.palette.warning.main }} />;
      case ConnectionErrorType.TIMEOUT:
        return <WifiOff sx={{ fontSize: 80, color: theme.palette.warning.main }} />;
      default:
        return <WifiOff sx={{ fontSize: 80, color: theme.palette.error.main }} />;
    }
  };

  // Get title based on error type
  const getTitle = () => {
    switch (errorType) {
      case ConnectionErrorType.NETWORK_ERROR:
        return 'Sin conexión a Internet';
      case ConnectionErrorType.BACKEND_UNAVAILABLE:
        return 'Servidor no disponible';
      case ConnectionErrorType.BACKEND_DEGRADED:
        return 'Servicio limitado';
      case ConnectionErrorType.TIMEOUT:
        return 'Conexión lenta';
      default:
        return 'Error de conexión';
    }
  };

  // Get description based on error type
  const getDescription = () => {
    switch (errorType) {
      case ConnectionErrorType.NETWORK_ERROR:
        return 'Verifica tu conexión a Internet y vuelve a intentarlo.';
      case ConnectionErrorType.BACKEND_UNAVAILABLE:
        return 'El servidor de la aplicación no responde. Puede estar detenido o en mantenimiento.';
      case ConnectionErrorType.BACKEND_DEGRADED:
        return 'Algunos servicios no están disponibles. La funcionalidad puede estar limitada.';
      case ConnectionErrorType.TIMEOUT:
        return 'El servidor tardó demasiado en responder. Puede estar sobrecargado.';
      default:
        return 'Ocurrió un error al conectar con el servidor.';
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 3
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <img
            src="/ai-flock-logo.png"
            alt="AIFlock"
            style={{
              height: '2.4rem',
              width: 'auto'
            }}
          />
        </Box>

        {/* Icon */}
        <Box
          sx={{
            width: 140,
            height: 140,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1
          }}
        >
          {getIcon()}
        </Box>

        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            fontSize: '1.75rem'
          }}
        >
          {getTitle()}
        </Typography>

        {/* Error Message */}
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '1.05rem',
            lineHeight: 1.6,
            maxWidth: 500
          }}
        >
          {errorMessage || getDescription()}
        </Typography>

        {/* Additional info for degraded service */}
        {errorType === ConnectionErrorType.BACKEND_DEGRADED && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.95rem',
              fontStyle: 'italic'
            }}
          >
            Es posible que algunas funciones no estén disponibles temporalmente.
          </Typography>
        )}

        {/* Retry Button */}
        <Button
          variant="contained"
          size="large"
          onClick={onRetry}
          disabled={isRetrying}
          startIcon={isRetrying ? <CircularProgress size={20} /> : <Refresh />}
          sx={{
            mt: 2,
            px: 4,
            py: 1.5,
            bgcolor: brandColor,
            '&:hover': {
              bgcolor: brandDark
            },
            '&:disabled': {
              bgcolor: alpha(brandColor, 0.4)
            },
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1.05rem'
          }}
        >
          {isRetrying ? 'Verificando conexión...' : 'Reintentar conexión'}
        </Button>

        {/* Retry count */}
        {retryCount > 0 && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.9rem',
              mt: 1
            }}
          >
            Intento {retryCount}
          </Typography>
        )}

        {/* Help text - conditional based on error type */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            width: '100%'
          }}
        >
          {errorType === ConnectionErrorType.BACKEND_UNAVAILABLE ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.9rem',
                  lineHeight: 1.6
                }}
              >
                {process.env.NODE_ENV === 'development' ? 'Posibles soluciones:' : 'Si el problema persiste:'}
              </Typography>
              <Box
                component="ul"
                sx={{
                  mt: 1.5,
                  pl: 3,
                  textAlign: 'left',
                  color: theme.palette.text.secondary,
                  fontSize: '0.85rem',
                  '& li': {
                    mb: 0.5
                  }
                }}
              >
                {process.env.NODE_ENV === 'development' ? (
                  <>
                    <li>Verifica que el servidor backend esté corriendo</li>
                    <li>Revisa que el servidor esté en el puerto correcto (normalmente 5678)</li>
                    <li>Verifica la configuración en el archivo .env</li>
                  </>
                ) : (
                  <>
                    <li>El servicio está temporalmente no disponible</li>
                    <li>Por favor, intenta nuevamente en unos minutos</li>
                    <li>Contacta al soporte si el problema persiste</li>
                  </>
                )}
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.9rem',
                  lineHeight: 1.6
                }}
              >
                Si el problema persiste, verifica:
              </Typography>
              <Box
                component="ul"
                sx={{
                  mt: 1.5,
                  pl: 3,
                  textAlign: 'left',
                  color: theme.palette.text.secondary,
                  fontSize: '0.85rem',
                  '& li': {
                    mb: 0.5
                  }
                }}
              >
                <li>Tu conexión a Internet está funcionando</li>
                <li>No hay restricciones de firewall o proxy</li>
                <li>La URL del servidor es correcta</li>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default ConnectionErrorScreen;
